import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import {
  dateSchema,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";
import { z } from "zod";
import { DEFAULT_CATEGORY_OPTIONS } from "../../a6/A6Transactions/config";
import { DEFAULT_C3_ASSET_CLASS_OPTIONS } from "../C3StaticData/edit-form-conf";
import { DepField, makeFiltersBuilder } from "@/features/FilterTable/utils";
import { createAssetClassFilterFields } from "@/features/FilterTable/assetClassFilterFields";
import { Tables } from "@/gen/tableNames/tables";
import { Clients } from "@/lib/clients";
import { useTranslation } from "react-i18next";

const filterC3TransactionsSchema = z.object({
  c3_portfolio_client_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  c3_portfolio_vehicle_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  c3_portfolio_account_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  c3_portfolio_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  c3_portfolio_bank_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  actif_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  category_contains: z.string().optional(),
  transfer_contains: z.string().optional(),
  fee_type_contains: z.string().optional(),
  description_contains: z.string().optional(),
  date_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  date_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  create_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  create_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  c3_static_datum_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  asset_class_1_equals: z.string().optional(),
  asset_class_2_equals: z.string().optional(),
});

const filterC3TransactionsFields = filterC3TransactionsSchema.keyof().enum;

export type FilterC3TransactionsSchema = z.infer<
  typeof filterC3TransactionsSchema
>;

const portfolioDepFields: readonly DepField<FilterC3TransactionsSchema>[] = [
  {
    name: filterC3TransactionsFields.c3_portfolio_bank_name_contains,
    key: "bank_name",
    extract: "text",
    bucket: "equals",
  },
  {
    name: filterC3TransactionsFields.c3_portfolio_client_contains,
    key: "client",
    extract: "text",
    bucket: "equals",
  },
  {
    name: filterC3TransactionsFields.c3_portfolio_vehicle_contains,
    key: "vehicle",
    extract: "text",
    bucket: "equals",
  },
  {
    name: filterC3TransactionsFields.c3_portfolio_name_contains,
    key: "id",
    extract: "id",
    bucket: "equals",
  },
  {
    name: filterC3TransactionsFields.c3_portfolio_account_name_contains,
    key: "account_name",
    extract: "text",
    bucket: "equals",
  },
];

export function FilterC3Transactions() {
  const { t, i18n } = useTranslation("tables");
  const tableName = Tables.C3_TRANSACTIONS;

  const fields: Field[] = [
    {
      name: filterC3TransactionsFields.c3_portfolio_client_contains,
      label: t(`${tableName}.searchbox.c3_portfolio_client_contains`, { defaultValue: "Client" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.C3}/${Tables.C3_PORTFOLIOS}`,
      column_name: "client",
      filters: makeFiltersBuilder<FilterC3TransactionsSchema>(
        portfolioDepFields,
        filterC3TransactionsFields.c3_portfolio_client_contains,
      ),
    },
    {
      name: filterC3TransactionsFields.c3_portfolio_vehicle_contains,
      label: t(`${tableName}.searchbox.c3_portfolio_vehicle_contains`, { defaultValue: "Vehicle / Beneficiary" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.C3}/${Tables.C3_PORTFOLIOS}`,
      column_name: "vehicle",
      filters: makeFiltersBuilder<FilterC3TransactionsSchema>(
        portfolioDepFields,
        filterC3TransactionsFields.c3_portfolio_vehicle_contains,
      ),
    },
    {
      name: filterC3TransactionsFields.c3_portfolio_account_name_contains,
      label: t(`${tableName}.searchbox.c3_portfolio_account_name_contains`, { defaultValue: "Account group" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.C3}/${Tables.C3_PORTFOLIOS}`,
      column_name: "account_name",
      filters: makeFiltersBuilder<FilterC3TransactionsSchema>(
        portfolioDepFields,
        filterC3TransactionsFields.c3_portfolio_account_name_contains,
      ),
    },
    {
      name: filterC3TransactionsFields.c3_portfolio_name_contains,
      label: t(`${tableName}.searchbox.c3_portfolio_name_contains`, { defaultValue: "Account" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.C3}/${Tables.C3_PORTFOLIOS}`,
      column_name: "name",
      filters: makeFiltersBuilder<FilterC3TransactionsSchema>(
        portfolioDepFields,
        filterC3TransactionsFields.c3_portfolio_name_contains,
      ),
    },
    {
      name: filterC3TransactionsFields.c3_portfolio_bank_name_contains,
      label: t(`${tableName}.searchbox.c3_portfolio_bank_name_contains`, { defaultValue: "Bank" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.C3}/${Tables.C3_PORTFOLIOS}`,
      column_name: "bank_name",
      filters: makeFiltersBuilder<FilterC3TransactionsSchema>(
        portfolioDepFields,
        filterC3TransactionsFields.c3_portfolio_bank_name_contains,
      ),
    },
    {
      name: filterC3TransactionsFields.actif_contains,
      label: t(`${tableName}.searchbox.actif_contains`, { defaultValue: "Asset" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.C3}/${Tables.C3_STATIC_DATA}`,
      column_name: "name",
    },
    ...createAssetClassFilterFields({
      assetClassFieldName: filterC3TransactionsFields.asset_class_1_equals,
      assetSubclassFieldName: filterC3TransactionsFields.asset_class_2_equals,
      assetClassLabel: t(`${tableName}.searchbox.asset_class_1_equals`, { defaultValue: "Asset class" }),
      assetSubclassLabel: t(`${tableName}.searchbox.asset_class_2_equals`, { defaultValue: "Sub asset class" }),
      client: Clients.C3,
      staticDataTableName: Tables.C3_STATIC_DATA,
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      assetSubclassOptions: DEFAULT_C3_ASSET_CLASS_OPTIONS,
    }),
    {
      name: filterC3TransactionsFields.category_contains,
      label: t(`${tableName}.searchbox.category_contains`, { defaultValue: "Transaction type" }),
      type: "sectionSelect",
      useTranslation: true,
      section_name_translation_prefix: "transactions.category_groups",
      translate_key: "category",
      client: Clients.C3,
      table_name: Tables.C3_TRANSACTIONS,
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      options: DEFAULT_CATEGORY_OPTIONS,
    },
    {
      name: filterC3TransactionsFields.transfer_contains,
      label: t(`${tableName}.searchbox.transfer_contains`, { defaultValue: "Transfer type" }),
      type: "select",
      useTranslation: true,
      client: Clients.C3,
      table_name: Tables.C3_TRANSACTIONS,
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      translate_key: "transfer",
    },
    {
      name: filterC3TransactionsFields.fee_type_contains,
      label: t(`${tableName}.searchbox.fee_type_contains`, { defaultValue: "Fee type" }),
      type: "select",
      useTranslation: true,
      client: Clients.C3,
      table_name: Tables.C3_TRANSACTIONS,
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      translate_key: "fee_type",
    },
    {
      name: filterC3TransactionsFields.description_contains,
      label: t(`${tableName}.searchbox.description_contains`, { defaultValue: "Description" }),
      type: "input",
    },
    {
      name: "date",
      type: "dateRange",
      label: t(`${tableName}.searchbox.date`, { defaultValue: "Value date" }),
      from: { name: filterC3TransactionsFields.date_from },
      to: { name: filterC3TransactionsFields.date_to },
      col_span: 2,
    },
    {
      name: "create",
      type: "dateRange",
      label: t(`${tableName}.searchbox.create`, { defaultValue: "Creation date" }),
      from: { name: filterC3TransactionsFields.create_from },
      to: { name: filterC3TransactionsFields.create_to },
      col_span: 2,
    },
  ];

  return (
    <FilterTable
      columns={2}
      client={Clients.C3}
      schema={filterC3TransactionsSchema}
      tableName={Tables.C3_TRANSACTIONS}
      fields={fields}
    />
  );
}
